import { useTranslation } from '../../../i18n/i18n';
import { ColumnsType } from 'antd/es/table';
import { Button, Space, Table, Typography } from 'antd';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { installationDataApi } from '../../../api/installation-data.api';
import type { JsonObject } from '@bufbuild/protobuf';
import dayjs from 'dayjs';
import './SensorsInstallationPage.scss';

interface TelemetryEntry {
  timestamp: string;
  device_uuid: string;
  topic: string;
  data?: { value?: number; unit?: string };
}

const { Text } = Typography;

const PAGE_SIZE = 10;
const PRIVATE_TOPIC_KEYWORD = 'private';
const PUBLIC_TOPIC_KEYWORD = 'public';

interface SensorsInstallationColumn {
  key: string;
  time: string;
  sensor: string;
  value: string;
}

const SensorsInstallationPage = () => {
  const { installationUuid } = useParams<{ installationUuid: string }>();
  const { t } = useTranslation('installation-location', {
    keyPrefix: 'installationLocationDetail.installation.sensors',
  });

  const [rawData, setRawData] = useState<JsonObject[]>([]);
  const [cursors, setCursors] = useState<string[]>(['']);
  const [cursorIndex, setCursorIndex] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!installationUuid) return;

    const fetchPage = async () => {
      setLoading(true);
      try {
        const result = await installationDataApi.getSewerLevelPage(installationUuid, cursors[cursorIndex], PAGE_SIZE);
        setRawData(result.data);
        setHasMore(result.hasMore);
        if (cursorIndex === cursors.length - 1 && result.nextCursor) {
          setCursors((prev) => [...prev.slice(0, cursorIndex + 1), result.nextCursor]);
        }
      } catch (error) {
        console.error('Failed to fetch telemetry:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPage();
  }, [installationUuid, cursorIndex, cursors]);

  const getSensorLabel = (topic: string, deviceUuid: string): string => {
    if (topic.includes(PRIVATE_TOPIC_KEYWORD)) return `${t('private')} - ${deviceUuid}`;
    if (topic.includes(PUBLIC_TOPIC_KEYWORD)) return `${t('public')} - ${deviceUuid}`;
    return `${t('unspecific')} - ${deviceUuid}`;
  };

  const rows: SensorsInstallationColumn[] = rawData.map((entry) => {
    const e = entry as unknown as TelemetryEntry;
    const rawValue = e.data?.value;
    const formatted = rawValue != null ? `${Math.round(rawValue * 100) / 100} ${e.data?.unit ?? ''}`.trim() : '-';

    return {
      key: `${e.timestamp}-${e.topic}`,
      time: dayjs(e.timestamp).format('DD/MM/YYYY HH:mm:ss'),
      sensor: getSensorLabel(e.topic ?? '', e.device_uuid),
      value: formatted,
    };
  });

  const columns: ColumnsType<SensorsInstallationColumn> = [
    {
      title: t('time'),
      dataIndex: 'time',
      key: 'time',
      render: (text: string) => <Text className={'MainTableText'}>{text}</Text>,
      width: '25%',
    },
    {
      title: t('sensor'),
      dataIndex: 'sensor',
      key: 'sensor',
      width: '50%',
    },
    {
      title: t('data'),
      dataIndex: 'value',
      key: 'value',
      width: '25%',
    },
  ];

  return (
    <>
      <Table
        size={'large'}
        columns={columns}
        dataSource={rows}
        rowKey={(record) => record.key}
        loading={loading}
        pagination={false}
      />
      <Space className={'SensorsPage__button'}>
        <Button onClick={() => setCursorIndex((i) => i - 1)} disabled={cursorIndex === 0 || loading}>
          {'<'}
        </Button>
        <Button onClick={() => setCursorIndex((i) => i + 1)} disabled={!hasMore || loading}>
          {'>'}
        </Button>
      </Space>
    </>
  );
};

export default SensorsInstallationPage;
